import { createContext, useContext, useRef, type ReactNode } from 'react';
import { useFeature } from '@growthbook/growthbook-react';

import useFeaturesReady from '@/shared/featureFlags/useFeaturesReady';
import { INVOICE_QR_GO_ENGINE_FLAG } from '../../constants/flag';
import {
  devForcedQrEngine,
  QR_ENGINE_BASE,
  resolveQrEngine,
  type QrEngine,
} from '../lib/engine';

interface QrEngineContext {
  engine: QrEngine;
  /** La base de la que sale la URL de TODAS las llamadas del flujo. */
  base: string;
  /**
   * `false` mientras el motor todavía no se congela. La página del dueño espera
   * a que sea `true`: su GET crea la página si no existe, y crearla en el motor
   * del default para luego cambiar de motor le deja una página huérfana.
   */
  resolved: boolean;
}

const Context = createContext<QrEngineContext | null>(null);

/**
 * El motor se resuelve UNA vez por sesión y se comparte.
 *
 * Si cada pantalla leyera la bandera por su cuenta y el valor cambiara a media
 * sesión, el cliente crearía su solicitud en un motor y la consultaría en el
 * otro: un 404 sobre su propia factura. Por eso, en cuanto las features están
 * listas, el valor se congela y ya no se vuelve a mirar.
 */
export const InvoiceQrEngineProvider = ({ children }: { children: ReactNode }) => {
  const featuresReady = useFeaturesReady();
  const goEngine = useFeature(INVOICE_QR_GO_ENGINE_FLAG);
  const frozen = useRef<QrEngine | null>(null);

  // Se congela en cuanto `useFeaturesReady` resuelve, aunque sea por timeout o
  // por error: la página del dueño espera `resolved`. Si GrowthBook no cargó, la
  // bandera llega como `unknownFeature` y el motor queda en el Go.
  const forcedEngine = devForcedQrEngine();
  if (frozen.current === null && (featuresReady || forcedEngine)) {
    frozen.current = resolveQrEngine(goEngine.source !== 'unknownFeature', goEngine.on, forcedEngine);
  }

  // Mientras las features no están listas se sirve el Go, que es donde vive el
  // flujo. No se congela todavía: congelar un default sin features dejaría el
  // interruptor sin efecto toda la sesión.
  const engine = frozen.current ?? 'go';

  return (
    <Context.Provider
      value={{ engine, base: QR_ENGINE_BASE[engine], resolved: frozen.current !== null }}>
      {children}
    </Context.Provider>
  );
};

/**
 * ⚠️ Las 14 rutas del mostrador se rigen por este valor y se mueven JUNTAS.
 *
 * Los dos motores guardan en tablas distintas y no comparten nada. Mover solo
 * la página pública es un bug grave y silencioso: el cliente crea la solicitud
 * en el Go, el dueño sigue mirando la bandeja del NestJS, y no le llega nada.
 * No hay error ni aviso; simplemente deja de facturar y se entera por un
 * cliente enojado.
 */
export const useInvoiceQrEngine = (): QrEngineContext => {
  const value = useContext(Context);
  // Fuera del provider se sirve el Go en vez de tronar: la página del mostrador
  // es anónima y no puede quedarse en blanco por un error de montaje.
  return value ?? { engine: 'go', base: QR_ENGINE_BASE.go, resolved: true };
};
